import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Checkbox from '../../components/ui/Checkbox';
import Toggle from '../../components/ui/Toggle';
import { Settings2 } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { updateCompanySettings } from '../../api/resources';
import { useCompanySettings } from '../../hooks/useCompanySettings';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

function WorkerItemRates() {
  // The switch that decides whether worker-wise rates exist at all.
  const { company, workerWiseRates } = useCompanySettings();
  const { companyId, role } = useAuth();
  const qc = useQueryClient();
  const { success, error: toastError } = useToast();
  const [switching, setSwitching] = useState(false);
  const isAdmin = role === 'ADMIN';

  const toggleWorkerWise = async (on) => {
    if (!company) return;
    setSwitching(true);
    try {
      await updateCompanySettings(companyId, { ...company, enableWorkerWiseRates: on });
      qc.invalidateQueries({ queryKey: ['company-settings'] });
      success(on
        ? 'Worker-wise rates are on. A worker\'s own rate is used where one is set.'
        : 'Worker-wise rates are off. Every job is paid the item\'s standard rate.');
    } catch (e) {
      toastError(e.response?.data?.message || 'Could not change this setting.');
    } finally {
      setSwitching(false);
    }
  };

  const navigate = useNavigate();
  const [allWorkers, setAllWorkers] = useState([]);
  const [allTailoringItems, setAllTailoringItems] = useState([]);
  const [workTypes, setWorkTypes] = useState(['CUTTING', 'STITCHING']); // from enum
  const [existingRates, setExistingRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [workerSearchTerm, setWorkerSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');

  // Saved-rates list: inline edits keyed by rate id, plus its own search box.
  const [savedEdits, setSavedEdits] = useState({});
  const [savedSearch, setSavedSearch] = useState('');
  const [savedBusyId, setSavedBusyId] = useState(null);

  // Selection states
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [selectedWorkTypeValues, setSelectedWorkTypeValues] = useState([]);

  // Filtered lists based on search
  const filteredWorkers = allWorkers.filter(worker =>
    ((worker.ledgerName || worker.name || '').toLowerCase().includes(workerSearchTerm.toLowerCase()) ||
    (worker.code || '').toLowerCase().includes(workerSearchTerm.toLowerCase()))
  );

  const filteredItems = allTailoringItems.filter(item =>
    item.name.toLowerCase().includes(itemSearchTerm.toLowerCase())
  );

  // Matrix data: each object represents a row in the table
  // We'll store the editable fields for each combination
  const [matrixData, setMatrixData] = useState([]);

  // Counts for display
  const selectedWorkersCount = selectedWorkerIds.length;
  const selectedItemsCount = selectedItemIds.length;
  const selectedWorkTypesCount = selectedWorkTypeValues.length;
  const generatedRowsCount = matrixData.length;

  // Fetch initial data
  useEffect(() => {
    fetchMatrixData();
  }, []);

  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiGet('/utility/worker-rates/matrix');
      setAllWorkers(data.workers);
      setAllTailoringItems(data.tailoringItems);
      setWorkTypes(data.workTypes);
      setExistingRates(data.existingRates);
      // Initialize matrix with existing rates? Or generate empty rows based on selections?
      // We'll generate rows based on selections, but pre-fill with existing rates if available.
      // For now, we'll generate empty rows and then fill in existing rates when selections change.
      // We'll handle that in the useEffect that generates matrixData.
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Regenerate matrix whenever selections or search terms change
  useEffect(() => {
    if (selectedWorkerIds.length === 0 || selectedItemIds.length === 0 || selectedWorkTypeValues.length === 0) {
      setMatrixData([]);
      return;
    }

    // Generate all combinations from FILTERED lists
    const combinations = [];
    for (const workerId of selectedWorkerIds) {
      // Only include if worker is in filtered list (passes search)
      const worker = allWorkers.find(w => w.id === workerId);
      if (!worker) continue;
      // Apply worker search filter
      const workerMatchesSearch = ((worker.ledgerName || worker.name || '').toLowerCase().includes(workerSearchTerm.toLowerCase()) ||
        (worker.code || '').toLowerCase().includes(workerSearchTerm.toLowerCase()));
      if (!workerMatchesSearch) continue;

      for (const itemId of selectedItemIds) {
        // Only include if item is in filtered list (passes search)
        const item = allTailoringItems.find(i => i.id === itemId);
        if (!item) continue;
        // Apply item search filter
        const itemMatchesSearch = item.name.toLowerCase().includes(itemSearchTerm.toLowerCase());
        if (!itemMatchesSearch) continue;

        for (const workType of selectedWorkTypeValues) {
          combinations.push({ workerId, tailoringItemId: itemId, workType });
        }
      }
    }

    // Map each combination to a matrix row, filling in existing rates if found
    const matrixRows = combinations.map(({ workerId, tailoringItemId, workType }) => {
      // Find existing rate for this combination
      const existing = existingRates.find(
        r => r.workerId === Number(workerId) &&
             r.tailoringItemId === Number(tailoringItemId) &&
             r.workType === workType
      );

      return {
        workerId,
        tailoringItemId,
        workType,
        // Editable fields
        rateType: existing ? existing.rateType : 'FIXED', // default to FIXED
        rateValue: existing ? existing.rateValue.toString() : '',
        effectiveDate: existing ? existing.effectiveDate : new Date().toISOString().split('T')[0],
        remark: existing ? existing.remark : '',
        // For display: we'll also store the names (we'll look them up below)
      };
    });

    setMatrixData(matrixRows);
  }, [selectedWorkerIds, selectedItemIds, selectedWorkTypeValues, workerSearchTerm, itemSearchTerm, allWorkers, allTailoringItems, existingRates]);

  // --- Saved rates list ---------------------------------------------------

  // Current value for a saved row: the pending edit if touched, else what's stored.
  const savedValue = (rate, field) =>
    savedEdits[rate.id]?.[field] ?? (
      field === 'rateValue' ? String(rate.rateValue)
        : field === 'effectiveDate' ? (rate.effectiveDate ? rate.effectiveDate.split('T')[0] : '')
        : field === 'remark' ? (rate.remark || '')
        : rate[field]
    );

  const editSaved = (id, field, value) => {
    setSavedEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const isSavedDirty = (id) => Boolean(savedEdits[id]);

  const handleUpdateSaved = async (rate) => {
    const rateValue = savedValue(rate, 'rateValue');
    if (rateValue === '' || Number.isNaN(parseFloat(rateValue))) {
      alert('Enter a valid rate.');
      return;
      
    }
    try {
      setSavedBusyId(rate.id);
      // Reuse bulk-save: it upserts on (company, worker, item, workType).
      await apiPost('/utility/worker-rates/bulk-save', {
        rates: [{
          workerId: rate.workerId,
          tailoringItemId: rate.tailoringItemId,
          workType: rate.workType,
          rateType: savedValue(rate, 'rateType'),
          rateValue: parseFloat(rateValue),
          effectiveDate: savedValue(rate, 'effectiveDate') || null,
          remark: savedValue(rate, 'remark') || null,
        }],
      });
      setSavedEdits(prev => {
        const next = { ...prev };
        delete next[rate.id];
        return next;
      });
      await fetchMatrixData();
    } catch (err) {
      alert('Could not update rate: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavedBusyId(null);
    }
  };

  const handleDeleteSaved = async (rate) => {
    const who = rate.worker?.name || `Worker ${rate.workerId}`;
    const what = rate.tailoringItem?.name || `Item ${rate.tailoringItemId}`;
    if (!window.confirm(`Delete the ${rate.workType.toLowerCase()} rate for ${who} on ${what}?`)) return;
    try {
      setSavedBusyId(rate.id);
      await apiDelete(`/utility/worker-rates/${rate.id}`);
      await fetchMatrixData();
    } catch (err) {
      alert('Could not delete rate: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavedBusyId(null);
    }
  };

  const visibleSavedRates = existingRates.filter((r) => {
    if (!savedSearch.trim()) return true;
    const q = savedSearch.toLowerCase();
    return (r.worker?.name || '').toLowerCase().includes(q)
      || (r.worker?.code || '').toLowerCase().includes(q)
      || (r.tailoringItem?.name || '').toLowerCase().includes(q);
  });

  // Handler for saving all rates
  const handleSaveAll = async () => {
    // Only send rows that actually have a numeric rate — a blank cell would
    // become NaN and fail the whole server-side transaction.
    const ratesToSave = matrixData
      .filter(row => row.rateValue !== '' && !Number.isNaN(parseFloat(row.rateValue)))
      .map(row => ({
        workerId: Number(row.workerId),
        tailoringItemId: Number(row.tailoringItemId),
        workType: row.workType,
        rateType: row.rateType,
        rateValue: parseFloat(row.rateValue),
        effectiveDate: row.effectiveDate || null,
        remark: row.remark || null
      }));

    if (ratesToSave.length === 0) {
      alert('Enter a rate for at least one row before saving.');
      return;
    }

    try {
      setSaving(true);
      const data = await apiPost('/utility/worker-rates/bulk-save', { rates: ratesToSave });
      await fetchMatrixData();
      alert(data.message || 'Rates saved successfully');
    } catch (err) {
      alert('Error saving rates: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-8 text-center">Loading...</div>;
  if (error) return <div className="py-8 text-center text-danger-500">{error}</div>;

  // Helper to get worker name by id
  const getWorkerName = (id) => {
    const worker = allWorkers.find(w => w.id === Number(id));
    return worker ? (worker.ledgerName || worker.name || `Worker ${id}`) : `Unknown (${id})`;
  };

  // Helper to get item name by id
  const getItemName = (id) => {
    const item = allTailoringItems.find(i => i.id === Number(id));
    return item ? item.name : `Unknown (${id})`;
  };

  return (
    <div>
      <PageHeader
        icon={Settings2}
        title="Worker & Item Rates"
        subtitle="Manage worker rates across tailoring items and work types"
        actions={[
          <Button key="back" variant="secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        ]}
      />

      {/* The switch, before anything else: it decides whether the rates below
          are used at all. */}
      <div className="mb-4 rounded-lg border border-line bg-surface-sunken p-3">
        <Toggle
          checked={workerWiseRates}
          disabled={!isAdmin || switching}
          onChange={toggleWorkerWise}
          label="Use worker-wise rates"
          hint={workerWiseRates
            ? 'On: a rate set for a worker below is used for their jobs, and the Orders screen lets an admin adjust the cost per job.'
            : "Off: every job is paid the item's standard cutting / stitching rate, and the per-worker rate box is hidden from Worker Allocation on the Orders screen. The rates below are kept for when you turn this back on."}
        />
        {!isAdmin && <p className="mt-1 pl-14 text-2xs text-content-subtle">Only an admin can change this.</p>}
      </div>

      {!workerWiseRates && (
        <div className="mb-4 rounded-lg border border-warning-500 bg-warning-50/40 px-3 py-2 text-xs text-content-muted">
          Worker-wise rates are switched off, so nothing set here affects what a worker is paid right now.
        </div>
      )}

      {/* Error message */}
      {error && <div className="mb-4 p-4 bg-danger-50 text-danger-500 rounded">{error}</div>}

      {/* Selection Panel */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Worker Selection Column */}
        <div>
          <h3 className="mb-2 font-semibold">Worker Selection</h3>
          <Input
            type="text"
            placeholder="Search workers..."
            className="mb-2 w-full"
            value={workerSearchTerm}
            onChange={(e) => setWorkerSearchTerm(e.target.value)}
          />
          <div className="flex items-center mb-2">
            <Checkbox
              checked={selectedWorkerIds.length === filteredWorkers.length && filteredWorkers.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedWorkerIds(filteredWorkers.map(w => w.id));
                } else {
                  setSelectedWorkerIds([]);
                }
              }}
            />
            <span className="ml-2">Check All</span>
          </div>
          <div className="max-h-60 overflow-y-auto border rounded p-2">
            {filteredWorkers.map(worker => (
              <div key={worker.id} className="flex items-center py-1">
                <Checkbox
                  checked={selectedWorkerIds.includes(worker.id)}
                  onChange={(e) => {
                    const id = worker.id;
                    if (e.target.checked) {
                      setSelectedWorkerIds([...selectedWorkerIds, id]);
                    } else {
                      setSelectedWorkerIds(selectedWorkerIds.filter(i => i !== id));
                    }
                  }}
                />
                <span className="ml-2 truncate max-w-xs">
                  {worker.ledgerName || worker.name} ({worker.code || 'N/A'})
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-sm text-content-muted">
            Selected: {selectedWorkersCount}
          </div>
        </div>

        {/* Item Selection Column */}
        <div>
          <h3 className="mb-2 font-semibold">Item Selection</h3>
          <Input
            type="text"
            placeholder="Search items..."
            className="mb-2 w-full"
            value={itemSearchTerm}
            onChange={(e) => setItemSearchTerm(e.target.value)}
          />
          <div className="flex items-center mb-2">
            <Checkbox
              checked={selectedItemIds.length === filteredItems.length && filteredItems.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedItemIds(filteredItems.map(i => i.id));
                } else {
                  setSelectedItemIds([]);
                }
              }}
            />
            <span className="ml-2">Check All</span>
          </div>
          <div className="max-h-60 overflow-y-auto border rounded p-2">
            {filteredItems.map(item => (
              <div key={item.id} className="flex items-center py-1">
                <Checkbox
                  checked={selectedItemIds.includes(item.id)}
                  onChange={(e) => {
                    const id = item.id;
                    if (e.target.checked) {
                      setSelectedItemIds([...selectedItemIds, id]);
                    } else {
                      setSelectedItemIds(selectedItemIds.filter(i => i !== id));
                    }
                  }}
                />
                <span className="ml-2 truncate max-w-xs">{item.name}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-sm text-content-muted">
            Selected: {selectedItemsCount}
          </div>
        </div>

        {/* Work Type Selection Column */}
        <div>
          <h3 className="mb-2 font-semibold">Work Type Selection</h3>
          <div className="flex items-center mb-2">
            <Checkbox
              checked={selectedWorkTypeValues.length === workTypes.length && workTypes.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedWorkTypeValues([...workTypes]);
                } else {
                  setSelectedWorkTypeValues([]);
                }
              }}
            />
            <span className="ml-2">Check All</span>
          </div>
          <div className="space-y-1">
            {workTypes.map(type => (
              <div key={type} className="flex items-center">
                <Checkbox
                  checked={selectedWorkTypeValues.includes(type)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedWorkTypeValues([...selectedWorkTypeValues, type]);
                    } else {
                      setSelectedWorkTypeValues(selectedWorkTypeValues.filter(t => t !== type));
                    }
                  }}
                />
                <span className="ml-2 capitalize">{type}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-sm text-content-muted">
            Selected: {selectedWorkTypesCount}
          </div>
        </div>
      </div>

      {/* Matrix Table */}
      {matrixData.length > 0 && (
        <p className="mb-2 text-xs text-content-muted">
          <strong>Fixed ₹</strong> pays the worker that flat amount.{' '}
          <strong>% of rate</strong> pays that percentage of the item's Customer Rate
          (set in Edit Item) — e.g. 12% of a ₹1000 item pays ₹120.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-sunken">
            <tr>
              <th className="th">Worker Name</th>
              <th className="th">Item Name</th>
              <th className="th">Work Type</th>
              <th className="th">Rate Type</th>
              <th className="th">Rate</th>
              <th className="th">Effective From</th>
              <th className="th">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {matrixData.map((row, index) => (
              <tr key={`${row.workerId}-${row.tailoringItemId}-${row.workType}`} className="border-t border-line hover:bg-surface-hover">
                <td className="td">{getWorkerName(row.workerId)}</td>
                <td className="td">{getItemName(row.tailoringItemId)}</td>
                <td className="td capitalize">{row.workType}</td>
                <td className="td">
                  <select
                    value={row.rateType}
                    onChange={(e) => {
                      setMatrixData(prev =>
                        prev.map((r, i) =>
                          i === index ? { ...r, rateType: e.target.value } : r
                        )
                      );
                    }}
                    className="input w-32"
                  >
                    <option value="FIXED">Fixed ₹</option>
                    <option value="PERCENTAGE">% of rate</option>
                  </select>
                </td>
                <td className="td">
                  <div className="flex items-center gap-1">
                    {row.rateType === 'FIXED' && <span className="text-content-subtle">₹</span>}
                    <input
                      type="number"
                      value={row.rateValue}
                      onChange={(e) => {
                        setMatrixData(prev =>
                          prev.map((r, i) =>
                            i === index
                              ? { ...r, rateValue: e.target.value }
                              : r
                          )
                        );
                      }}
                      className="input input-bordered w-full"
                      placeholder="0.00"
                    />
                    {row.rateType === 'PERCENTAGE' && <span className="text-content-subtle">%</span>}
                  </div>
                </td>
                <td className="td">
                  <input
                    type="date"
                    value={row.effectiveDate}
                    onChange={(e) => {
                      setMatrixData(prev =>
                        prev.map((r, i) =>
                          i === index
                            ? { ...r, effectiveDate: e.target.value }
                            : r
                        )
                      );
                    }}
                    className="input input-bordered w-full"
                  />
                </td>
                <td className="td">
                  <input
                    type="text"
                    value={row.remark}
                    onChange={(e) => {
                      setMatrixData(prev =>
                        prev.map((r, i) =>
                          i === index
                            ? { ...r, remark: e.target.value }
                            : r
                        )
                      );
                    }}
                    className="input input-bordered w-full"
                    placeholder="Optional remarks"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary and Save Button */}
      <div className="mt-6 flex justify-between items-center">
        <div className="text-sm text-content-muted">
          {selectedWorkersCount} workers × {selectedItemsCount} items × {selectedWorkTypesCount} work types = {generatedRowsCount} rates
        </div>
        <Button
          onClick={handleSaveAll}
          variant="primary"
          // Disable if no selections or matrix is empty
          disabled={saving || generatedRowsCount === 0}
        >
          {saving ? 'Saving...' : 'Save All Rates'}
        </Button>
      </div>

      {/* Saved rates — editable in place */}
      <div className="mt-10 border-t border-line pt-6">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-content">Saved Worker Rates</h3>
            <p className="text-xs text-content-muted">
              {existingRates.length} rate{existingRates.length === 1 ? '' : 's'} configured. Edit any field and press Update.
            </p>
          </div>
          <Input
            type="text"
            placeholder="Search saved rates…"
            className="w-64"
            value={savedSearch}
            onChange={(e) => setSavedSearch(e.target.value)}
          />
        </div>

        {existingRates.length === 0 ? (
          <p className="py-6 text-center text-sm text-content-subtle">
            No rates saved yet. Use the matrix above to add some.
          </p>
        ) : visibleSavedRates.length === 0 ? (
          <p className="py-6 text-center text-sm text-content-subtle">No saved rates match “{savedSearch}”.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-sunken">
                <tr>
                  <th className="th">Worker</th>
                  <th className="th">Item</th>
                  <th className="th">Work Type</th>
                  <th className="th">Rate Type</th>
                  <th className="th">Rate</th>
                  <th className="th">Effective From</th>
                  <th className="th">Remarks</th>
                  <th className="th text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleSavedRates.map((rate) => {
                  const dirty = isSavedDirty(rate.id);
                  const busy = savedBusyId === rate.id;
                  return (
                    <tr key={rate.id} className={`border-t border-line ${dirty ? 'bg-warning-50' : 'hover:bg-surface-hover'}`}>
                      <td className="td font-medium text-content">
                        {rate.worker?.name || `Worker ${rate.workerId}`}
                        {rate.worker?.code && <span className="ml-1 text-xs text-content-subtle">({rate.worker.code})</span>}
                      </td>
                      <td className="td">{rate.tailoringItem?.name || `Item ${rate.tailoringItemId}`}</td>
                      <td className="td capitalize">{rate.workType.toLowerCase()}</td>
                      <td className="td">
                        <select
                          className="input w-32"
                          value={savedValue(rate, 'rateType')}
                          onChange={(e) => editSaved(rate.id, 'rateType', e.target.value)}
                        >
                          <option value="FIXED">Fixed ₹</option>
                          <option value="PERCENTAGE">% of rate</option>
                        </select>
                      </td>
                      <td className="td">
                        <div className="flex items-center gap-1">
                          {savedValue(rate, 'rateType') === 'FIXED' && <span className="text-content-subtle">₹</span>}
                          <input
                            type="number"
                            step="0.01"
                            className="input w-24"
                            value={savedValue(rate, 'rateValue')}
                            onChange={(e) => editSaved(rate.id, 'rateValue', e.target.value)}
                          />
                          {savedValue(rate, 'rateType') === 'PERCENTAGE' && <span className="text-content-subtle">%</span>}
                        </div>
                      </td>
                      <td className="td">
                        <input
                          type="date"
                          className="input w-36"
                          value={savedValue(rate, 'effectiveDate')}
                          onChange={(e) => editSaved(rate.id, 'effectiveDate', e.target.value)}
                        />
                      </td>
                      <td className="td">
                        <input
                          type="text"
                          className="input w-full"
                          placeholder="—"
                          value={savedValue(rate, 'remark')}
                          onChange={(e) => editSaved(rate.id, 'remark', e.target.value)}
                        />
                      </td>
                      <td className="td text-right whitespace-nowrap">
                        <button
                          type="button"
                          className="rounded-md px-2 py-1 text-xs font-semibold text-brand-600 hover:bg-brand-50 disabled:opacity-40"
                          disabled={!dirty || busy}
                          onClick={() => handleUpdateSaved(rate)}
                        >
                          {busy ? '…' : 'Update'}
                        </button>
                        <button
                          type="button"
                          className="rounded-md px-2 py-1 text-xs font-semibold text-danger-500 hover:bg-danger-50 disabled:opacity-40"
                          disabled={busy}
                          onClick={() => handleDeleteSaved(rate)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkerItemRates;